import { useEventListener } from "@vueuse/core";
import { toValue, watch } from "vue";
import { useHostElement } from "#ce/composables/useHostElement";
import { FORM_RESET_EVENT } from "#ce/framework/events";
import { isFormAssociated } from "#ce/framework/utils";

export function useForm() {
  const host = useHostElement();

  let internals = null;
  let form = null;

  // `attachInternals()` throws on a second call, so the registration wrapper
  // owns the single attachment and exposes it as `formInternals`. On a
  // non-form-associated host `internals` stays null and every operation here
  // is a no-op.
  if (host && isFormAssociated(host.constructor)) {
    internals = host.formInternals ?? null;
    form = internals?.form ?? null;
  } else if (host && import.meta.env.DEV) {
    // A nested piece called a form composable on a host that didn't opt in, so
    // form submission silently no-ops. The form-association contract test
    // (framework/form-association.spec.js) enforces the opt-in at build time;
    // this warns at runtime for compositions the static walk can't see.
    console.warn(
      `[useForm] <${host.localName}> uses a form composable but is not form-associated — ` +
        "declare it with defineOptions(formAssociatedHost()).",
    );
  }

  /**
   * Updates the form value.
   *
   * @param {any} value - The value to set.
   * @param {*} options
   * @param {boolean} [options.emit] - Whether to emit a change event. Defaults to `true`.
   */
  function setValue(value, options) {
    internals?.setFormValue(toValue(value));

    // Dispatched even without internals (jsdom has no attachInternals), so the unit tier sees the
    // same change events a browser does and a silent-mirror assertion can actually fail.
    if (options?.emit !== false) {
      emitChange();
    }
  }

  /**
   * Dispatches the element's `change` event. An inner control's own `change` is not composed, so it
   * stops at the shadow boundary and the host has to report the change itself.
   *
   * Use this when the element owns the timing — a native control reports the user's change and
   * stays silent for a scripted one, which `setValue(value, { emit: false })` leaves to the caller.
   */
  function emitChange() {
    host?.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /**
   * Mirrors a model into the form value — silently, and synchronously with the write, so the
   * form data is already current inside a listener of the element's own `input`/`change`. The
   * current value is mirrored immediately; the element owns every `change` dispatch itself.
   *
   * @param {import('vue').WatchSource} source - The model carrying the form value.
   */
  function mirrorFormValue(source) {
    watch(source, (value) => setValue(value, { emit: false }), { immediate: true, flush: "sync" });
  }

  /**
   * Keeps the element's `valueMissing` validity mirrored — synchronously, so `checkValidity()`
   * is already current inside a listener of the element's own events.
   *
   * @param {import('vue').MaybeRefOrGetter<boolean>} missing - Whether a required value is missing.
   * @param {import('vue').MaybeRefOrGetter<string>} message - The message reported while missing.
   * @param {import('vue').MaybeRefOrGetter<HTMLElement|null|undefined>} [anchor] - Element the validation bubble points at.
   */
  function mirrorValueMissing(missing, message, anchor) {
    // The anchor is a template ref, so a control that starts invalid runs the immediate pass
    // before its inner element exists; watching the anchor re-applies the validity once it does,
    // rather than leaving the browser's bubble pointing at the host.
    watch(
      [() => toValue(missing), () => toValue(message), () => toValue(anchor)],
      ([isMissing, resolvedMessage, resolvedAnchor]) => {
        if (isMissing) {
          setValidity({ valueMissing: true }, resolvedMessage, resolvedAnchor ?? undefined);
        } else {
          setValidity({});
        }
      },
      { immediate: true, flush: "sync" },
    );
  }

  /**
   * Reports the element's validity to its form, matching a native control:
   * an invalid element blocks submission, fails `form.checkValidity()`, and
   * matches `:invalid`.
   *
   * @param {Partial<Record<keyof ValidityStateFlags, boolean>>} flags - Validity flags. Pass `{}` to clear.
   * @param {string} [message] - Message shown when the browser reports the failure. Required by any set flag.
   * @param {HTMLElement} [anchor] - Element the browser points its validation bubble at.
   */
  function setValidity(flags, message, anchor) {
    internals?.setValidity(flags, message, anchor);
  }

  /**
   * Runs the callback when the owning form is reset, so the element can restore
   * the state its attributes describe.
   *
   * @param {() => void} callback
   */
  function onFormReset(callback) {
    useEventListener(host, FORM_RESET_EVENT, callback);
  }

  return {
    // Properties
    internals,
    form,

    // Methods
    setValue,
    emitChange,
    mirrorFormValue,
    mirrorValueMissing,
    setValidity,
    onFormReset,
  };
}
